import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControlLabel,
  Checkbox,
  LinearProgress
} from '@mui/material';
import {
  Sync as SyncIcon,
  CloudSync as CloudSyncIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface SyncRecord {
  sync_date: string;
  sync_timestamp: string;
  sync_type: string;
  status: string;
  students_synced: number;
  teachers_synced: number;
  attendance_records_synced: number;
  errors_count: number;
  duration_seconds: number | null;
  log_message: string;
}

interface SyncStatus {
  recent_syncs: SyncRecord[];
  current_stats: {
    total_students: number;
    total_teachers: number;
    recent_student_attendance: number;
    recent_teacher_attendance: number;
  };
}

const AttendanceSyncManager: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [forceSync, setForceSync] = useState(false);
  const [syncProgress, setSyncProgress] = useState<string>('');

  useEffect(() => {
    fetchSyncStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSyncStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSyncStatus = async () => {
    try {
      setError(null);
      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.getSyncStatus();

      if (response.data.status === 'success') {
        setSyncStatus(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load sync status');
      }
    } catch (err: any) {
      console.error('Sync status error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load sync status');
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async () => {
    try {
      setSyncing(true);
      setSyncProgress('Initiating sync...');
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.triggerSync({
        start_date: selectedDate.toISOString().split('T')[0],
      });

      const result = response.data;

      if (result.status === 'success') {
        setSyncProgress('Sync completed successfully!');

        // Show success details
        const data = result.data;
        setSyncProgress(
          `✅ Sync completed!\n` +
          `Students: ${data.students_synced || 0}\n` +
          `Teachers: ${data.teachers_synced || 0}\n` +
          `Student attendance: ${data.student_attendance_synced || 0}\n` +
          `Teacher attendance: ${data.teacher_attendance_synced || 0}`
        );

        // Refresh status after 2 seconds
        setTimeout(() => {
          fetchSyncStatus();
          setSyncDialogOpen(false);
          setSyncProgress('');
        }, 3000);

      } else if (result.status === 'warning') {
        setSyncProgress(`⚠️ ${result.message}`);
      } else {
        throw new Error(result.message || 'Sync failed');
      }

    } catch (err) {
      setSyncProgress(`❌ Sync failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return <SuccessIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'partial':
        return <WarningIcon color="warning" />;
      case 'in_progress':
        return <CircularProgress size={20} />;
      default:
        return <SyncIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      case 'partial':
        return 'warning';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={fetchSyncStatus}>
          <RefreshIcon /> Retry
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Attendance Sync Management
      </Typography>

      {/* Current Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Students
              </Typography>
              <Typography variant="h3" color="primary">
                {syncStatus?.current_stats.total_students || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Enrolled in system
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Teachers
              </Typography>
              <Typography variant="h3" color="primary">
                {syncStatus?.current_stats.total_teachers || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active in system
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Student Records
              </Typography>
              <Typography variant="h3" color="success.main">
                {syncStatus?.current_stats.recent_student_attendance || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last 7 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Teacher Records
              </Typography>
              <Typography variant="h3" color="success.main">
                {syncStatus?.current_stats.recent_teacher_attendance || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last 7 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sync Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Manual Sync Controls
            </Typography>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={() => setSyncDialogOpen(true)}
              disabled={syncing}
              color="primary"
            >
              Trigger Sync
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary">
            Manually trigger attendance synchronization from the SIS system.
            This will sync students, teachers, and attendance records for the selected date.
          </Typography>
        </CardContent>
      </Card>

      {/* Recent Sync History */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Recent Sync History
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchSyncStatus}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {syncStatus?.recent_syncs && syncStatus.recent_syncs.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Students</TableCell>
                    <TableCell align="right">Teachers</TableCell>
                    <TableCell align="right">Attendance</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncStatus.recent_syncs.map((sync, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {new Date(sync.sync_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(sync.sync_timestamp).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sync.sync_type.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getStatusIcon(sync.status)}
                          <Chip
                            label={sync.status}
                            color={getStatusColor(sync.status)}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">{sync.students_synced}</TableCell>
                      <TableCell align="right">{sync.teachers_synced}</TableCell>
                      <TableCell align="right">{sync.attendance_records_synced}</TableCell>
                      <TableCell align="right">{formatDuration(sync.duration_seconds)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                          {sync.log_message}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No sync history available. Trigger your first sync to see records here.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => !syncing && setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CloudSyncIcon sx={{ mr: 1 }} />
            Trigger Attendance Sync
          </Box>
        </DialogTitle>
        <DialogContent>
          {syncing ? (
            <Box>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                Syncing attendance data...
              </Typography>
              {syncProgress && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{syncProgress}</pre>
                </Alert>
              )}
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" gutterBottom>
                Select a date to sync attendance data from the SIS system:
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Sync Date"
                    value={selectedDate}
                    onChange={(newValue) => newValue && setSelectedDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={forceSync}
                    onChange={(e) => setForceSync(e.target.checked)}
                  />
                }
                label="Force sync (override existing data)"
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                This will sync students, teachers, and attendance records for {selectedDate.toLocaleDateString()}.
                {forceSync && ' Existing data will be overwritten.'}
              </Alert>

              {syncProgress && (
                <Alert
                  severity={syncProgress.includes('❌') ? 'error' : syncProgress.includes('⚠️') ? 'warning' : 'success'}
                  sx={{ mt: 2 }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{syncProgress}</pre>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button
            onClick={triggerSync}
            variant="contained"
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
          >
            {syncing ? 'Syncing...' : 'Start Sync'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendanceSyncManager;